import { threeSixtyKeys } from '../constants/newCars';
import { ButtonType, SliderType } from './LandingPageTypes';

export enum MODEL_WIDGETS {
  HeroSection = 'hero_section',
  MediaIcons = 'media_icons',
  OverView = 'overview_widget',
  SpecificationTabBar = 'specification_tab_bar',
  KeySpecifications = 'key_specification',
  ExclusiveFeatures = 'exclusive_features',
  SpecificationAndFeatures = 'specification_and_features',
  VariantList = 'variant_list',
  ColorVariant = 'color_variant',
  MaintenanceWidget = 'maintenance_widget',
  RatingWidget = 'rating_widget',
  MediaPopup = 'media_pop_up',
  VariantListPopup = 'variant_list_pop_up',
  CarComparison = 'car_comparison',
  CityListPopup = 'city_pop_up',
  VehicleDetailsWidget = 'vehicle_details',
  RCDetailsWidget = 'rc_details_widget',
  TestDrive = 'test_drive_widget',
  Story = 'story_widget',
  Slider = 'slider',
  BannerWidget = "banner_widget"
}

export enum MediaTypes {
  Image = 1,
  Video = 2,
}

export enum RCStatus {
  Expired = 'negative',
  Active = 'positive',
  Warning = 'warning',
}

export type HeroList = {
  url: string;
  mediaType: MediaTypes;
  thumbnailUrl?: string;
};

export type HeroSectionType = {
  widgetType: MODEL_WIDGETS.HeroSection;
  title: string;
  shareLink: string;
  list: HeroList[];
};

export enum VariantMapping {
  Search = 'search',
  VariantList = 'variant_list',
}

export enum CityListPopupMapping {
  Search = 'search',
  PopularCityList = 'popular_city_list',
  AllCities = 'all_city_list',
}

export enum MediaIconMapping {
  ImageIcon = 'image_icon',
  VideoIcon = 'video_icon',
  ColorIcon = 'color_icon',
  PanoView = '360_icon',
}

export enum MaintenanceWidgetMapping {
  Heading = 'heading',
  VariantDropdown = 'variant_list_dropdown',
  FuelRangeSlider = 'fuel_maintenance_range_slider',
}

export enum MediaMapping {
  AppBar = 'app_bar',
  NestedSlider = 'nested_slider',
}

export enum VariantListPopupMapping {
  Filter = 'filter_widget',
  VariantList = 'variant_list',
}

export enum BrandAndSimilarModelPopupMapping {
  SimilarModelList = 'similar_model_list',
  Brands = 'brands',
  Search = 'search',
  VariantListPopup = 'variant_list_pop_up',
}

export type MediaIconsListType = {
  title?: string;
  type: MediaIconMapping;
  icon?: string;
};

export type MediaIconsType = {
  widgetType: MODEL_WIDGETS.MediaIcons;
  list: MediaIconsListType[];
};

export type ParkplusOwnersType = {
  icon: string;
  title: string;
};

export type OverviewType = {
  widgetType: MODEL_WIDGETS.OverView;
  title: string;
  ratingText: string;
  ratingCount: number;
  rating: number;
  parkplusOwners: ParkplusOwnersType;
  modelName: string;
  discontinued: boolean;
  expectedLaunch: string;
  priceTitle: string;
  priceSubtitle: string;
};

export type SpecificationTabBarListType = {
  scrollTo: string;
  title: string;
};

export type SpecificationTabBarType = {
  widgetType: MODEL_WIDGETS.SpecificationTabBar;
  list: SpecificationTabBarListType[];
};

export type KeySpecificationsListType = {
  title: string;
  subtitle: string;
  image: string;
};

export type KeySpecificationsType = {
  widgetType: MODEL_WIDGETS.KeySpecifications;
  list: KeySpecificationsListType[];
  title: string;
};

export type ExclusiveFeaturesListType = {
  image: string;
  title: string;
};

export type ExclusiveFeaturesType = {
  widgetType: MODEL_WIDGETS.ExclusiveFeatures;
  title: string;
  list: ExclusiveFeaturesListType[];
};

export type SpecificationAndFeaturesType = {
  widgetType: MODEL_WIDGETS.SpecificationAndFeatures;
  title: string;
  limit: number;
  button: ButtonType;
};

export type VariantListType = {
  widgetType: MODEL_WIDGETS.VariantList;
  limit: number;
  button: ButtonType;
  title: string;
};

export type ColorVariantListType = {
  carImage: string;
  colorList: string[];
  subtitle: string;
  title: string;
};

export type ColorVariantType = {
  widgetType: MODEL_WIDGETS.ColorVariant;
  title: string;
  popupTitle: string;
  appBarTitle: string;
  list: ColorVariantListType[];
};

export type HeadingType = {
  title: string;
  type: MaintenanceWidgetMapping.Heading;
};

export type MaintenanceVariantType = {
  title: string;
  type: MaintenanceWidgetMapping.VariantDropdown;
};

export type FuelDataType = {
  maxValueSlider: number;
  minValueSlider: number;
  resultHeading: string;
  resultSuffix: string;
  subtitle: string;
  title: string;
  type: MaintenanceWidgetMapping.FuelRangeSlider;
};

export type MaintenanceWidgetType = {
  widgetType: MODEL_WIDGETS.MaintenanceWidget;
  list: (HeadingType | MaintenanceVariantType | FuelDataType)[];
};

export type RatingType = {
  widgetType: MODEL_WIDGETS.RatingWidget;
  rating: number;
  ratingCount: number;
  title: string;
  list: {
    rating: number;
    title: string;
  }[];
};

export type MediaPopupListDataType = {
  description: string;
  mediaLabel: string;
  mediaType: string;
  subtitle: string;
  title: string;
  url: string;
  thumbnailUrl?: string;
};

export type MediaPopupMediaListType = {
  data: MediaPopupListDataType[];
  title: string;
};

export type MediaPopupListType = {
  icon: string;
  title: string;
  type: MediaMapping;
  carouselType?: string;
  list?: {
    [key: string]: MediaPopupMediaListType;
  };
  panoImages: {
    [threeSixtyKeys.Interior]: string[];
    [threeSixtyKeys.Exterior]: string[];
  };
};

export type MediaPopupType = {
  widgetType: MODEL_WIDGETS.MediaPopup;
  list: MediaPopupListType[];
};

export type VariantPopupListType = {
  key: string;
  title: string;
  type: string;
  value: string;
};

export type VariantPopupFilterType = {
  title: string;
  list: VariantPopupListType[];
};

export type VariantListPopupDataType = {
  type: VariantListPopupMapping;
  title?: string;
  list?: VariantPopupFilterType[];
};

export type VariantListPopupType = {
  widgetType: MODEL_WIDGETS.VariantListPopup;
  list: VariantListPopupDataType[];
};

export type BrandAndSimilarListDataType = {
  image?: string;
  modelId?: number;
  title: string;
  brandId?: number;
  type?: BrandAndSimilarModelPopupMapping;
  icon?: string;
  widgetType?: string;
  list?: [];
  brandImage?: string;
  discontinued?: boolean;
};

export type BrandAndSimilarModelListType = {
  type: BrandAndSimilarModelPopupMapping;
  title: string;
  list?: BrandAndSimilarListDataType[];
  icon?: string;
  widgetType?: string;
};

export type VariantSpecificationListType = {
  key: string;
  modelId: number;
  specId: number;
  value: string;
  variantId: number;
  highlightRow?: boolean;
};

export type VariantSpecificationType = {
  image: string;
  title: string;
  data: VariantSpecificationListType[];
  highlightTitle?: boolean;
};

export type EmiType = {
  title: string;
  subtitle: string;
  emi: number;
  months: number;
  interestRate: number;
  displayValue: string;
  downPayment: number;
  downPaymentText: string;
};

export type VariantListCardType = {
  priceSubtitle: string;
  priceTitle: string;
  subtitle: string;
  title: string;
};

export type TestDriveWidgetType = {
  widgetType: MODEL_WIDGETS.TestDrive;
  count: number;
};

export type VariantListsDataType = {
  id: number;
  variantId: number;
  name: string;
  emi?: EmiType;
  exShowRoomPrice?: string;
  image: string;
  fuelMaintenanceRangeSlider?: {
    multiplicationFactor: number;
  };
  fuelType?: string;
  onRoadPrice?: number;
  transmissionType?: number;
  price?: {
    title: string;
    subtitle: string;
  };
  waitingPeriod?: {
    icon: string;
    title: string;
  };
  variantListCard: VariantListCardType;
  modelId?: number;
  brandName: string;
  modelName: string;
  discontinued?: boolean;
  title?: string;
  subtitle?: string;
  variantName?: string;
  priceTitle?: string;
  priceSubtitle?: string;
  subTitle?: string;
  icon?;
  other: string;
  rto: string;
  insurance: string;
};

export type SimilarModelListType = {
  modelId: number;
  variantId: number;
  brandName: string;
  modelName: string;
  image: string;
  variantListCard: VariantListCardType;
};

export type CarComparisonType = {
  widgetType: MODEL_WIDGETS.CarComparison;
  title: string;
  list: number[];
};

export type CityListType = {
  image?: string;
  cityId: number;
  title: string;
};

export type CityListDataType = {
  title: string;
  type: CityListPopupMapping;
  list?: CityListType[];
};

export type CityListPopupType = {
  widgetType: MODEL_WIDGETS;
  list: CityListDataType[];
};

export type VehicleAlertsType = {
  icon: string;
  title: string;
  subtitle: string;
};

export type VehicleDetailsType = {
  widgetType: MODEL_WIDGETS.VehicleDetailsWidget;
  data: {
    alerts: VehicleAlertsType[];
    bottomStrip: {
      title: string;
      cta: {
        screenId: number;
      };
    };
    modelDetails: {
      brandLogo: string;
      rating: number;
      modelImage: string;
      modelName: string;
      vehicleNumber: string;
    };
    ownerDetails: {
      title: string;
      subtitle: string;
      description: string;
    };
    registrationDetails: {
      title: string;
      subtitle: string;
    };
  };
};

export type AccordionListDataType = {
  title: string;
  subtitle: string;
  description: string;
  statusType?: RCStatus;
  statusLabel?: string;
};

export type AccordionListType = {
  title: string;
  icon: string;
  list: AccordionListDataType[];
};

export type RCDetailsType = {
  widgetType: MODEL_WIDGETS.RCDetailsWidget;
  title: string;
  list: AccordionListType[];
};

export type VariantDetailsType = {
  specificationAndFeatures: VariantSpecificationType[];
  localeDetails: {
    exShowroomPrice: string;
    insurance: string;
    onRoadPrice: string;
    other: string;
    rto: string;
    emi: EmiType;
    price: {
      title: string;
      subtitle: string;
    };
  };
};

export type StoryType = {
  story: string;
  title: string;
  widgetType: MODEL_WIDGETS.Story;
};

export type ModelDetailsListsType =
  | HeroSectionType
  | MediaIconsType
  | OverviewType
  | SpecificationTabBarType
  | KeySpecificationsType
  | ExclusiveFeaturesType
  | SpecificationAndFeaturesType
  | VariantListType
  | ColorVariantType
  | MaintenanceWidgetType
  | VariantListPopupType
  | MediaPopupType
  | RatingType
  | CarComparisonType
  | VehicleDetailsType
  | RCDetailsType
  | TestDriveWidgetType
  | StoryType
  | SliderType
  | BannerWidgetType;

export type ModelDetailsType = {
  list: ModelDetailsListsType[];
  variantList: VariantListsDataType[];
  originalVariantList: VariantListsDataType[];
  similarModelList: {
    [key: string]: SimilarModelListType[];
  };
  brandId: number;
  modelId: number;
  brochureUrl: string;
};

export type BannersList = {
  redirectionUrl : string;
  bannerUrl: string;
};

export type BannerWidgetType = {
  widgetType: MODEL_WIDGETS.BannerWidget;
  banners: Array<BannersList>
};